import { useState } from 'react';
import { useScrollRevealGroup } from '../../../hooks/useScrollReveal';
import { caseStudies } from '../../../mocks/portfolio';

export default function TourismCaseStudiesSection() {
  const groupRef = useScrollRevealGroup();

  return (
    <section id="tourism-work" className="relative pt-24 pb-16 bg-ds-bg-2 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ds-red/30 to-transparent" />
      <div className="absolute top-1/2 right-0 w-80 h-80 bg-ds-red/5 blur-[100px] rounded-full pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6" ref={groupRef}>
        {/* Header */}
        <div className="mb-12 reveal">
          <span className="section-label">Tourism Website Portfolio</span>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mt-3">
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl text-white leading-none">
              REAL TOURISM
              <br />
              <span className="text-ds-red neon-text-red">RESULTS.</span>
            </h2>
            <p className="font-body text-white/50 text-sm leading-relaxed max-w-xs">
              Every client below is a Queensland tourism operator. Every result is real.
            </p>
          </div>
          <div className="mt-8 h-px w-24 bg-ds-red reveal" style={{ boxShadow: '0 0 12px rgba(180,255,57,0.4)' }} />
        </div>

        {/* Cards */}
        <div className="space-y-4 reveal">
          {caseStudies.map((study) => (
            <CaseStudyCard key={study.id} study={study} />
          ))}
        </div>
      </div>
    </section>
  );
}

function CaseStudyCard({ study }: { study: typeof caseStudies[0] }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="grid lg:grid-cols-5 border border-ds-border bg-ds-bg overflow-hidden group hover:border-ds-red/30 transition-colors duration-300"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Image */}
      <div className="lg:col-span-2 relative overflow-hidden h-52 lg:h-auto min-h-[200px]">
        {study.screenshotUrl ? (
          <div className="absolute inset-0 flex items-center justify-center bg-[#0d0d0d] px-6 py-5">
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 60%, rgba(0,160,160,0.10) 0%, transparent 70%)' }}
            />
            <div className="relative w-full max-w-[340px]">
              <div
                className="relative rounded-t-lg overflow-hidden"
                style={{
                  background: '#1c1c1e',
                  padding: '8px 8px 5px',
                  border: '1px solid #2e2e30',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
                }}
              >
                <div className="flex justify-center mb-1.5">
                  <div className="w-1 h-1 rounded-full" style={{ background: '#3a3a3c' }} />
                </div>
                <div
                  className={`overflow-hidden transition-transform duration-700 ${hovered ? 'scale-[1.02]' : 'scale-100'}`}
                  style={{ borderRadius: '2px', border: '1px solid #111' }}
                >
                  <img
                    src={study.screenshotUrl}
                    alt={`${study.client} website`}
                    className="w-full block"
                    style={{ maxHeight: '160px', objectFit: 'cover', objectPosition: 'top' }}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </div>
              <div style={{ height: '3px', background: '#141416' }} />
              <div
                style={{
                  height: '12px',
                  background: 'linear-gradient(to bottom, #1c1c1e, #161618)',
                  borderRadius: '0 0 6px 6px',
                  border: '1px solid #2a2a2c',
                  borderTop: 'none',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.8)',
                }}
              />
            </div>
          </div>
        ) : (
          <img
            src={study.image}
            alt={study.client}
            className={`w-full h-full object-cover object-top transition-transform duration-700 ${hovered ? 'scale-105' : 'scale-100'}`}
            loading="lazy"
            decoding="async"
          />
        )}
        <div className="absolute top-4 left-4 flex items-center gap-2 bg-ds-bg/80 border border-ds-red/30 px-2.5 py-1" style={{ backdropFilter: 'blur(8px)' }}>
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i className={`${study.industryTag} text-ds-red text-xs`} />
          </div>
          <span className="font-body text-white/70 text-xs tracking-wider uppercase">{study.industry}</span>
        </div>
      </div>

      {/* Content */}
      <div className="lg:col-span-3 p-7 lg:p-9 flex flex-col justify-center relative">
        <div className="absolute top-0 left-0 bottom-0 w-px bg-gradient-to-b from-transparent via-ds-red/25 to-transparent hidden lg:block" />
        <span className="font-body text-ds-red text-xs tracking-[0.3em] uppercase mb-2">Case Study</span>
        <h3 className="font-heading font-900 text-white text-2xl leading-tight mb-2">{study.client}</h3>
        <p className="font-display text-ds-red text-base mb-4" style={{ lineHeight: '1.2' }}>{study.headline}</p>
        <p className="font-body text-white/45 text-xs leading-relaxed mb-5 line-clamp-3">
          <strong className="text-white/60">Solution:</strong> {study.solution}
        </p>
        <div className="grid grid-cols-3 gap-2 mb-5">
          {study.results.map((r) => (
            <div key={r.label} className="bg-ds-bg-2 border border-ds-border p-3 text-center">
              <div className="font-display text-xl text-ds-red">{r.stat}</div>
              <div className="font-body text-white/35 text-xs mt-0.5 leading-tight">{r.label}</div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {study.services.map((s) => (
            <span key={s} className="font-body text-xs text-white/45 border border-ds-border px-2.5 py-1">
              {s}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
